import {
  API_ROUTE_PREFIX_HTTPS,
  API_ROUTE_V2_PREFIX_HTTPS,
} from "@tm/api-utils";
import {
  DATE_FORMATS,
  FormFieldType,
  FORM_FIELDS_MAP,
  isOnServer,
  LOB,
  PARTNER,
  PartnerType,
  PARTNER_DOMAIN_NAME,
  TRIPMONEY_DOMAIN_NAME,
  UTM_PARAMS,
} from "@tm/isomorphic-utils";
import { getHostName } from "../../utils";
import { formatDate } from "./dateUtils";

export const setURLQueryParams = (
  params: Record<string, string | number | boolean> = {}
) => {
  const usp = new URLSearchParams();
  Object.keys(params).forEach((e) => {
    if (params[e]) {
      usp.set(e, params[e].toString());
    }
  });
  return usp.toString();
};

export const persistUtmParams = (queryParam: URLSearchParams) => {
  const utm_source = queryParam.get(UTM_PARAMS.UTM_SOURCE);
  const utm_medium = queryParam.get(UTM_PARAMS.UTM_MEDIUM);
  const utm_campaign = queryParam.get(UTM_PARAMS.UTM_CAMPAIGN);

  if (utm_source) {
    sessionStorage.setItem(UTM_PARAMS.UTM_SOURCE, utm_source);
  }

  if (utm_medium) {
    sessionStorage.setItem(UTM_PARAMS.UTM_MEDIUM, utm_medium);
  }

  if (utm_campaign) {
    sessionStorage.setItem(UTM_PARAMS.UTM_INS_CAMPAIGN, utm_campaign);
  }
};

/**
 * Resolves internal_source from URL params / sessionStorage / hostname-based
 * heuristics and persists the result to sessionStorage (+ URL when a
 * hostname-based fallback is applied).
 *
 * @param currentValue – value already available in the component (e.g. from GSSP)
 * @param options.hostnameRules – ordered hostname→value rules applied when no
 *   URL/session value is found. When a rule matches the URL is also updated
 *   via replaceHistory.
 * @returns the resolved internal_source value
 */
export const resolveAndPersistInternalSource = (
  currentValue = "",
  { hostnameRules = [] as { hostnames: string[]; value: string }[] } = {}
): string => {
  if (typeof window === "undefined") return currentValue;

  const url = new URL(window.location.href);
  const cmpFromUrl = url.searchParams.get("cmp");
  const internalFromUrl = url.searchParams.get("internal_source");

  let updated = currentValue;
  if (cmpFromUrl) {
    updated = cmpFromUrl;
  } else if (internalFromUrl) {
    updated = internalFromUrl;
  }

  if (updated === "" && hostnameRules.length > 0) {
    const referrer = document.referrer;
    let hostname = "";
    if (referrer) {
      try {
        hostname = new URL(referrer).hostname;
      } catch {
        // invalid referrer URL
      }
    }
    if (!hostname) {
      hostname = url.hostname;
    }

    for (const rule of hostnameRules) {
      if (rule.hostnames.some((h) => hostname.endsWith(h))) {
        updated = rule.value;
      }
    }
  }
  sessionStorage.setItem("internal_source", updated);
  replaceHistory("internal_source", updated);
  return updated;
};

export const replaceHistory = (param: string, value: string) => {
  if (typeof window !== "undefined") {
    const baseUrl = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    value?.length > 0 ? searchParams.set(param, value) : null;
    const url = `${baseUrl}${
      searchParams.toString().length > 0 ? "?" + searchParams : ""
    }`;
    window.history.replaceState(
      { ...window.history.state, as: url, url: url },
      "",
      url
    );
  }
};

export const replaceHistoryRemoveParams = (param: string) => {
  const baseUrl = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.delete(param);

  const url = `${baseUrl}${
    searchParams.toString().length > 0 ? "?" + searchParams : ""
  }`;
  window.history.replaceState(
    { ...window.history.state, as: url, url: url },
    "",
    url
  );
};

export const getCookieByName = (cookieName: string) => {
  if (isOnServer()) {
    return;
  }
  const cookie: Record<string, string> = {};
  document.cookie.split(";").forEach((el) => {
    const [key, value] = el.split("=");
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export function setCookie(name: string, value: string, milliseconds = 0) {
  const d = new Date();
  d.setTime(d.getTime() + milliseconds);
  const expires = "expires=" + d.toUTCString();
  document.cookie = `${name}=${value};${expires};path=/`;
}

export const getFormFieldErrorMsg = (
  field: FormFieldType,
  errorMsg?: string
) => {
  if (errorMsg) {
    return errorMsg;
  } else {
    const fieldName = FORM_FIELDS_MAP[field] || field;
    return `Please enter ${fieldName}`;
  }
};

export const isDobValid = (value: string | Date, regex?: string) => {
  if (!value) return false;
  const formattedDate = formatDate(value, DATE_FORMATS["DD/MM/YYYY"]);
  const isValidDate = new RegExp(regex || "").test(formattedDate);
  return isValidDate;
};

const MY_BUSINESS_SUBSCRIPTION_COOKIE = "myBusinessSubscription";
const VERTICAL_COOKIE = "vertical";
const MMT_AUTH_COOKIE = "mmt-auth";
const MY_PARTNER_COOKIE_VALUE = "myPartner";
const MY_BIZ_COOKIE_VALUE = "b2b";

export const getPartnerOnClientSide = () => {
  const hostname = getHostName() || "";
  if (hostname.includes(PARTNER_DOMAIN_NAME.MYPARTNER)) {
    return PARTNER.MYPARTNER;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.MYBIZ)) {
    return PARTNER.MYBIZ;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.MMT)) {
    return PARTNER.MMT;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.GI)) {
    return PARTNER.GI;
  } else if (hostname.includes(PARTNER_DOMAIN_NAME.TRIPMONEY)) {
    return PARTNER.TRIPMONEY;
  } else {
    console.error(
      `Error detecting partner. Unsupported domain hostname - ${hostname}`
    );
    return PARTNER.TRIPMONEY;
  }
};

export const isPartnerMMT = () => {
  return getPartnerOnClientSide() === PARTNER.MMT;
};

export const isPartnerGI = () => {
  return getPartnerOnClientSide() === PARTNER.GI;
};

// Original global helpers (hostname-based) – used across the app
export const isMyPartner = () => {
  return getPartnerOnClientSide() === PARTNER.MYPARTNER;
};

export const isMyBiz = () => {
  return getPartnerOnClientSide() === PARTNER.MYBIZ;
};

export const isFieldValid = (regex: string, updatedField: string): boolean => {
  try {
    const validator = new RegExp(regex || "");
    return validator?.test(updatedField);
  } catch (error) {
    return false;
  }
};

export const isHostMakemytrip = () => {
  if (isOnServer()) return false;
  const url = new URL(window.location.href);
  return TRIPMONEY_DOMAIN_NAME.MMT.includes(url.hostname);
};

/**
 * Redirects users when critical page data is missing, based on the current hostname.
 *
 * - If host is mybiz.makemytrip.com → redirect to www.makemytrip.com with the same path & query
 * - If host is mypartner.makemytrip.com → redirect to https://mypartner.makemytrip.com/
 * - For any other host → fallback to https://www.makemytrip.com/
 */

// Internal helpers for redirect logic only (cookie + auth based)
const isMyPartnerForRedirect = () => {
  const myBusinessSubscription = getCookieByName(
    MY_BUSINESS_SUBSCRIPTION_COOKIE
  );
  const vertical = getCookieByName(VERTICAL_COOKIE);
  const isPartnerByHost = getPartnerOnClientSide() === PARTNER.MYPARTNER;

  return (
    (myBusinessSubscription === MY_PARTNER_COOKIE_VALUE ||
      vertical === MY_PARTNER_COOKIE_VALUE) &&
    isPartnerByHost
  );
};

const isMyBizForRedirect = () => {
  const cookieForMyBiz = getCookieByName(MY_BUSINESS_SUBSCRIPTION_COOKIE);
  const mmtAuthVal = getCookieByName(MMT_AUTH_COOKIE);
  const isMyBizUserByAuth = mmtAuthVal?.charAt(mmtAuthVal.length - 1);

  if (cookieForMyBiz && cookieForMyBiz === MY_BIZ_COOKIE_VALUE) {
    return true;
  }

  if (isMyBizUserByAuth === "C") {
    return true;
  }

  return false;
};

export const redirectOnMissingDataByHost = (data: unknown) => {
  try {
    const currentUrl = new URL(window.location.href);
    const { hostname, pathname } = currentUrl;

    // Correct mismatched host vs cookies/auth first
    if (!isMyPartnerForRedirect() && hostname === "mypartner.makemytrip.com") {
      window.location.href = "https://mypartner.makemytrip.com/";
      return;
    }

    if (isMyBizForRedirect() && hostname === "www.makemytrip.com") {
      window.location.href = "https://www.makemytrip.com/";
      return;
    }

    if (!isMyBizForRedirect() && hostname === "mybiz.makemytrip.com") {
      window.location.href = "https://www.makemytrip.com/travel-insurance/";
      return;
    }
    // If we actually have data, do not redirect further
    if (data) return;

    if (hostname === PARTNER_DOMAIN_NAME.MYBIZ) {
      window.location.href = `https://www.makemytrip.com/`;
      return;
    }

    if (hostname === PARTNER_DOMAIN_NAME.MYPARTNER) {
      window.location.href = "https://mypartner.makemytrip.com";
      return;
    }

    // GI AND MMT HANDLE
    if (hostname === "www.goibibo.com" && pathname !== "/insurance") {
      window.location.href = "https://www.goibibo.com/insurance";
      return;
    }

    if (
      hostname === "www.makemytrip.com" &&
      pathname !== "/travel-insurance/"
    ) {
      window.location.href = "https://www.makemytrip.com/travel-insurance/";
      return;
    }
  } catch {
    // swallow errors to avoid breaking page
  }
};

/**
 * Sets up a timeout to redirect when data is missing, and returns
 * a cleanup function that clears the timeout.
 */
export const scheduleRedirectOnMissingDataByHost = (
  data: unknown,
  delayMs = 100
): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  timer = setTimeout(() => {
    redirectOnMissingDataByHost(data);
  }, delayMs);

  return () => {
    if (timer) {
      clearTimeout(timer);
    }
  };
};

export const debounce = <T extends any[]>(
  func: (...args: T) => void,
  delay: number
) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const generateSourceSetImageUrl = (
  cdnUrlWidths: Array<number>,
  imgUrl: string,
  width: number
) => {
  let imgWidths = cdnUrlWidths;
  if (!imgWidths || !Array.isArray(imgWidths) || imgWidths.length === 0)
    imgWidths = [width, width * 2, width * 3];

  return imgWidths
    .map((width) => `${imgUrl}?im=Resize=(${width}) ${width}w`)
    .join(",");
};

export const isObject = (item: any) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

export function deepMergeObjects(target: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!target[key]) target[key] = {};
        target[key] = deepMergeObjects(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    });
  }

  return deepMergeObjects(target, ...sources);
}

export type Region = "normal" | "WW" | "EU" | "SEA" | "WWEUC";

export type HeightData = {
  [key: number]: {
    [key in Region]: number[];
  };
};

//TODO: Check for a better way to handle the Dynamic size of cells in react-window
export const interpolateRowHeight = (
  width: number,
  region: Region,
  expansionLevel = 0
) => {
  const heightData: HeightData = {
    360: {
      normal: [57],
      WW: [57, 159],
      EU: [57, 183, 399],
      SEA: [57, 207, 207],
      WWEUC: [65.5, 215.5, 215],
    },
    370: {
      normal: [57],
      WW: [57, 159],
      SEA: [57, 183, 183],
      EU: [57, 183, 375],
      WWEUC: [65.5, 215.5, 191.5],
    },
    315: {
      normal: [57],
      WW: [57, 183],
      SEA: [57, 207, 231],
      EU: [57, 207, 471],
      WWEUC: [65.5, 215.5, 215.5],
    },
    354: {
      normal: [57],
      WW: [57, 159],
      SEA: [57, 207, 207],
      EU: [57, 183, 399],
      WWEUC: [65.5, 215.5, 215.5],
    },
    300: {
      normal: [57],
      WW: [57, 183],
      SEA: [57, 231, 231],
      EU: [57, 207, 495],
      WWEUC: [65.5, 239.5, 215.5],
    },
    708: {
      normal: [57],
      WW: [57, 135],
      SEA: [57, 159, 159],
      EU: [57, 159, 255],
      WWEUC: [65.5, 167.5, 143.5],
    },
    480: {
      normal: [57],
      WW: [57, 135],
      SEA: [57, 183, 159],
      EU: [57, 183, 303],
      WWEUC: [65.5, 215.5, 191.5],
    },
  };

  const availableWidths = Object.keys(heightData)
    .map(Number)
    .sort((a, b) => a - b);

  const findNearestWidths = (targetWidth: number) => {
    let lowerWidth = availableWidths[0];
    let upperWidth = availableWidths[availableWidths.length - 1];

    for (let i = 0; i < availableWidths.length - 1; i++) {
      if (
        availableWidths[i] <= targetWidth &&
        availableWidths[i + 1] >= targetWidth
      ) {
        lowerWidth = availableWidths[i];
        upperWidth = availableWidths[i + 1];
        break;
      }
    }
    return [lowerWidth, upperWidth];
  };

  const [lowerWidth, upperWidth] = findNearestWidths(width);

  const lowerHeights = heightData?.[lowerWidth]?.[region] || [57];
  const upperHeights = heightData?.[upperWidth]?.[region] || [57];

  const lowerHeight =
    lowerHeights[Math.min(expansionLevel, lowerHeights.length - 1)];
  const upperHeight =
    upperHeights[Math.min(expansionLevel, upperHeights.length - 1)];

  const interpolatedHeight =
    lowerHeight +
    ((upperHeight - lowerHeight) * (width - lowerWidth)) /
      (upperWidth - lowerWidth);

  return interpolatedHeight;
};

export const COUNTRY_BANNER_CONFIG = {
  AE: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/UAE_New.png",
  },
  GB: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/UnitedKingdom.png",
  },
  KW: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/Kuwait.png",
  },
  BH: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/Bahrain.png",
  },
  OM: {
    bannerImage: "https://promos.makemytrip.com/Growth/Images/B2C/GCC/Oman.png",
  },
  QA: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/Qatar.png",
  },
  SA: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/HP/KSA/ksabg2.jpg",
  },
  WW: {
    bannerImage:
      "https://promos.makemytrip.com/Growth/Images/B2C/GCC/Worldwide.png",
  },
};

export function getRandomBgImage(index?: number | string | null) {
  // const cookieTag: string | undefined = getCookieByName("InsBgImgTag");
  // if (
  //   cookieTag &&
  //   COUNTRY_BANNER_CONFIG[cookieTag as keyof typeof COUNTRY_BANNER_CONFIG]
  // ) {
  //   return COUNTRY_BANNER_CONFIG[
  //     cookieTag as keyof typeof COUNTRY_BANNER_CONFIG
  //   ].bannerImage;
  // }
  // const keys = Object.keys(COUNTRY_BANNER_CONFIG);
  // const randomKey = keys[Math.floor(Math.random() * keys.length)];
  // // setCookie("InsBgImgTag", randomKey, 24 * 60 * 60 * 1000);
  // return COUNTRY_BANNER_CONFIG[randomKey as keyof typeof COUNTRY_BANNER_CONFIG]
  //   .bannerImage;
  const randomKey = Math.floor(Math.random() * 7);
  const images = ["bg1", "bg2", "bg3", "bg4", "bg5", "bg6", "bg7"];
  let bgImageIndex = null;
  if (typeof window !== "undefined") {
    bgImageIndex = parseInt(localStorage.getItem("bgImageIndex") || "0");
    if (!bgImageIndex && !index) {
      bgImageIndex = randomKey;
    } else if (index && !bgImageIndex) {
      bgImageIndex = parseInt(index.toString());
    }
    localStorage.setItem("bgImageIndex", bgImageIndex.toString(10));
    setCookie("bgImageIndex", bgImageIndex.toString(10), 24 * 60 * 60 * 1000);
    return `https://imgak.mmtcdn.com/pwa_v3/pwa_commons_assets/desktop/${images[bgImageIndex]}.jpg`;
  }

  if (index) {
    bgImageIndex = parseInt(index.toString());
  }
  if (bgImageIndex === null) {
    bgImageIndex = randomKey;
  }
  return `https://imgak.mmtcdn.com/pwa_v3/pwa_commons_assets/desktop/${images[bgImageIndex]}.jpg`;
  // return null;
}

export const getLobOnClientSide = (partner: PartnerType) => {
  if (isOnServer()) {
    return LOB.STANDALONE;
  }
  if (partner === PARTNER.MYPARTNER) {
    return LOB.MYPARTNER;
  } else if (partner === PARTNER.MYBIZ) {
    return LOB.STANDALONE_MYBIZ;
  }
  return LOB.STANDALONE;
};

export const getPathPartnerOnClientSide = (partner: PartnerType) => {
  if (partner === PARTNER.MYPARTNER || partner === PARTNER.MYBIZ) {
    return PARTNER.MMT;
  }
  return partner;
};

export const getHTTPSRoutePrefixOnClientSide = (partner: PartnerType) => {
  if (partner === PARTNER.MYPARTNER || partner === PARTNER.MYBIZ) {
    return API_ROUTE_V2_PREFIX_HTTPS;
  }
  return API_ROUTE_PREFIX_HTTPS;
};

export function hydrateVars(
  input: any,
  overrideVars: { [key: string]: string | number | null } = {}
): any {
  if (Array.isArray(input)) {
    return input.map((item) => hydrateVars(item, overrideVars));
  } else if (typeof input === "object" && input !== null) {
    const hydratedObject: any = {};
    for (const key in input) {
      if (key === "vars" && typeof input[key] === "object") {
        for (const varKey in input[key]) {
          const varValue = overrideVars[varKey] || input[key][varKey];
          for (const innerKey in hydratedObject) {
            if (typeof hydratedObject[innerKey] === "string") {
              hydratedObject[innerKey] = hydratedObject[innerKey].replace(
                `{{${varKey}}}`,
                varValue
              );
            }
          }
        }
      } else {
        hydratedObject[key] = hydrateVars(input[key], overrideVars);
      }
    }
    return hydratedObject;
  } else if (typeof input === "string") {
    return input.replace(/\{\{([^}]+)\}\}/g, (match, p1) =>
      overrideVars && p1.trim() in overrideVars
        ? String(overrideVars[p1.trim()])
        : match
    );
  }
  return input;
}

export const getThemePartner = (partner: PartnerType) => {
  if (partner === PARTNER.MYPARTNER) {
    return PARTNER.MMT;
  }
  return partner;
};
